<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { useAuthStore } from '@/stores/auth'
import ValidationErrors from '@/components/common/ValidationErrors.vue'
import ValidationErrorsClass from '@/assets/js/ValidationErrors.js'
import XButton from '@/components/common/XButton.vue'
import { useToggleThemeStore } from "@/stores/toggleTheme"


const themeStore = useToggleThemeStore()

// Props
const props = defineProps({
  height: {
    type: String,
    default: 'h-screen',
  },
})

// Router and stores
const router = useRouter()
const authStore = useAuthStore()

// Reactive state
const username = ref(null)
const password = ref(null)
const isLoading = ref(false)
const validationErrors = ref(null)

// GraphQL mutation
const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput) {
    login(input: $input) {
      access_token
      refresh_token
      expires_in
      token_type
      user {
        id
        email
        userDetails {
          agreedToTerms
        }
      }
    }
  }
`

const { mutate: loginMutation } = useMutation(LOGIN_MUTATION)

// Methods
const initValidationErrors = () => {
  return new ValidationErrorsClass()
}

const setValidationErrors = (error) => {
  let errors = initValidationErrors()
  errors.extractErrorsFrom(error)
  validationErrors.value = errors.errors
}

const clearValidationErrors = () => {
  validationErrors.value = null
}

const login = async () => {
  isLoading.value = true
  clearValidationErrors()
  
  try {
    const { data } = await loginMutation({
      input: {
        username: username.value,
        password: password.value,
      },
    })
    
    isLoading.value = false
    
    // Add authentication
    authStore.addAuth(data.login.access_token, data.login.user)
    
    // Navigate based on terms agreement
    if (data.login.user.userDetails.agreedToTerms) {
      router.push({ name: 'Dashboard' })
    } else {
      router.push({ name: 'Account' })
    }
  } catch (error) {
    isLoading.value = false
    setValidationErrors(error)
    console.error(error)
  }
}

const detectEnterKey = (event) => {
  if (event.code === 'Enter') {
    login()
  }
}
</script>

<template>
  <div
    class="flex flex-1 flex-col items-center justify-center px-6 py-12 lg:px-8 overflow-hidden relative z-100"
    :class="{ [height]: true }"
  >
    <div class="mx-auto w-full sm:max-w-sm">
      <img
        class="mx-auto h-14 w-auto rounded-[0.25rem]"
        src="@/assets/images/ytz-logo.svg"
        alt="YTZ company logo"
      />
    </div>

    <div class="mt-10 mx-auto w-full max-w-xs md:max-w-sm">
      <form class="space-y-6" @submit.prevent="login">
        <div>
          <label for="email" class="block text-sm">Email address</label>
          <div class="mt-2">
            <input
              v-model="username"
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              class="!text-base-content dark:bg-base-300"
              required
              @keyup="detectEnterKey"
            />
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between">
            <label for="password" class="block text-sm">Password</label>
            <div class="text-sm">
              <router-link
                :to="{ name: 'ForgotPassword' }"
                class="text-accent hover:text-accent/80 outline-accent ring-none "
              >
                Forgot password?
              </router-link>
            </div>
          </div>
          <div class="mt-2">
            <input
              v-model="password"
              id="password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
              class="!text-base-content dark:bg-base-300"
              @keyup="detectEnterKey"
            />
          </div>
        </div>

        <div>
          <XButton
            @click.prevent="login"
            color="primary"
            type="submit"
            class="w-full"
            :isLoading="isLoading"
          >
            Sign in
          </XButton>
        </div>
      </form>

      <ValidationErrors
        v-if="validationErrors"
        :errors="validationErrors"
        bannerClass="mt-3"
        variant="error"
        :maxErrors="1"
      />

      <p class="mt-10 text-center text-sm">
        Not a member?

        <router-link
          :to="{ name: 'Register' }"
          class="font-semibold leading-6 text-accent hover:text-accent/80 outline-accent"
        >
          Register Now
        </router-link>
      </p>
    </div>
  </div>
</template>

<style scoped>
input[name="email"],
input[name="password"] {
  @apply block w-full rounded-md border-0 px-2.5 py-1.5 placeholder:text-base-300/40 focus:ring-2 focus:ring-accent outline-none sm:text-sm sm:leading-6 text-base-300;
}
</style>